import {
	AngleDown,
	AngleUp,
	ChartIcon, Dropdown, Input, Label, Menu, Option,
} from '@aleph/shared-components';
import {
	ChangeEvent, FC, useCallback, useState,
} from 'react';
import ChartsPlaceholder from 'wb-reddit/assets/charts.svg';
import { DASHBOARD_KPI_OPTIONS } from 'wb-spotify/app/routes/superGrid/actionRow/charts/utils';
import { debounce } from 'lodash';
import { useMutation } from '@apollo/client';
import UpdateClientDashboardSettingsDocument from 'wb-spotify/app/routes/superGrid/cellRenderers/name/api/updateClientDashboardSettings.graphql';
import { ICellRendererParams } from 'ag-grid-community';
import { useTranslation } from 'react-i18next';
import {
	GRID_DASHBOARD_BREAKDOWNS,
} from 'wb-spotify/app/routes/superGrid/cellRenderers/tool/settingsBreakdownSelector';

type KpiDropdownProps = ICellRendererParams & {
	clientId?: string
};

const KpiDropdown: FC<KpiDropdownProps> = ({ clientId, ...params }) => {
	const { t } = useTranslation();
	const [updateKpiDocument, { loading }] = useMutation(UpdateClientDashboardSettingsDocument);
	const selectedBreakdown = params?.data?.clientBreakdown;

	const [options, setOptions] = useState(DASHBOARD_KPI_OPTIONS);
	const [searchQuery, setSearchQuery] = useState('');

	function onOptionSelect(option: Option) {
		return async () => {
			if (typeof clientId === 'string') {
				const newKpi = option?.value?.toString?.();

				await updateKpiDocument({
					variables: {
						clientId,
						clientKpi:       newKpi,
						clientBreakdown: selectedBreakdown ?? GRID_DASHBOARD_BREAKDOWNS[0].value,
					},
				});

				params?.node?.setData({
					...params?.data,
					clientKpi:       newKpi,
					clientBreakdown: selectedBreakdown ?? GRID_DASHBOARD_BREAKDOWNS[0].value,
				});
			}
		};
	}

	const onOptionSearch = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			setOptions(
				curr => {
					if (e.target.value) {
						return curr.filter(option => option.label.toLowerCase().includes(e.target.value.toLowerCase()));
					}

					return DASHBOARD_KPI_OPTIONS;
				},
			);
		},
		[],
	);

	const debouncedOptionSearch = useCallback(
		debounce(onOptionSearch, 300),
		[onOptionSearch],
	);

	function onInputChange(e: ChangeEvent<HTMLInputElement>) {
		debouncedOptionSearch(e);
		setSearchQuery(e.target.value);
	}

	return (
		<div className="w-full h-full relative flex items-center justify-center">
			<img
				src={ChartsPlaceholder}
				className="absolute"
				alt="charts-placeholder"
			/>

			<div
				className="w-[184px] h-[48px] p-[8px] bg-white border-none z-10"
			>
				<Dropdown
					focusOnTrigger
					usePortal
					placement="bottom-start"
					onClose={() => {
						setOptions(DASHBOARD_KPI_OPTIONS);
						setSearchQuery('');
					}}
					triggerControl={
						({ isOpen }) => (
							<Input
								disabled={loading}
								value={searchQuery}
								onChange={onInputChange}
								label={t('sc: kpi.select_main', 'Select main KPI')}
								prefix={
									<ChartIcon
										width={16}
										height={16}
									/>
								}
								placeholder={t('sc: kpi.search', 'Search KPIs')}
								suffix={
									isOpen
										? (
											<AngleUp
												width={16}
												height={16}
											/>
										)
										: (
											<AngleDown
												width={16}
												height={16}
											/>
										)
								}
							/>
						)
					}
				>
					<Menu
						showMaxItems={5}
						estimatedMaxHeight={160}
					>
						{
							options.map(option => (
								<Menu.Item onClick={onOptionSelect(option)}>
									<Label>
										{option.label}
									</Label>
								</Menu.Item>
							))
						}
					</Menu>
				</Dropdown>
			</div>
		</div>
	);
};

export default KpiDropdown;
